// Shared page-settings nav definition — the single source of truth for the
// per-page settings sections, their grouping, and icons. Consumed by
// PageSettingsPage.vue (slug validation + the current section's title) and
// Sidebar.vue (the main sidebar's page-settings mode), so the two surfaces
// can't drift apart. Mirrors the shape of config/knowledgeNav.ts.
//
// Unlike the settings / knowledge / inbox variants, every route here is scoped
// to ONE page, so the route helpers take the page id.
import type { Component } from 'vue'
import { PhArrowLeft, PhFileText, PhChartBar, PhCode } from '@phosphor-icons/vue'
import i18n from '@/i18n'

export interface PageSettingsNavItemDef {
  /** Slug — also the URL segment (/:pageId/settings/:id). 'info' lives at the bare URL. */
  id: string
  label: string
  icon: Component
}

export interface PageSettingsNavGroupDef {
  label: string
  items: PageSettingsNavItemDef[]
}

/** Icon for the "back to the editor" row in the sidebar's page-settings mode. */
export const PAGE_SETTINGS_BACK_ICON = PhArrowLeft

export const PAGE_SETTINGS_NAV_GROUPS: PageSettingsNavGroupDef[] = [
  {
    get label() { return i18n.global.t('page.settingsNav.groupLabel') },
    items: [
      { id: 'info', get label() { return i18n.global.t('page.settingsNav.infoLabel') }, icon: PhFileText },
      { id: 'analytics', get label() { return i18n.global.t('settings.analytics') }, icon: PhChartBar },
      { id: 'code', get label() { return i18n.global.t('page.settingsNav.customCode') }, icon: PhCode },
    ],
  },
]

/** Flat list of every slug, in nav order. */
export const PAGE_SETTINGS_SLUGS: string[] = PAGE_SETTINGS_NAV_GROUPS.flatMap(g => g.items.map(i => i.id))

/** Is this a known page-settings slug? */
export function isPageSettingsSlug(id: string): boolean {
  return PAGE_SETTINGS_SLUGS.includes(id)
}

/** Flat item lookup by slug. */
export function findPageSettingsItem(id: string): PageSettingsNavItemDef | null {
  for (const group of PAGE_SETTINGS_NAV_GROUPS) {
    const item = group.items.find(i => i.id === id)
    if (item) return item
  }
  return null
}

/**
 * Workspace-relative route for a page-settings section — the sidebar prefixes
 * the workspace, exactly like settingsItemRoute. Page info lives at the bare
 * /:pageId/settings URL (same idea as General living at bare /settings).
 */
export function pageSettingsItemRoute(pageId: string, id: string): string {
  return id === 'info' ? `/${pageId}/settings` : `/${pageId}/settings/${id}`
}

/** Workspace-relative route back to the builder for this page. */
export function pageEditorRoute(pageId: string): string {
  return `/${pageId}/editor`
}
